// Soulstack Studio site root: the full route table from the design prototype
// (marketing, docs, showcase, play, auth, app, project workspace) on real
// history routing. The editor is the 13-mode shell at /app/editor/:id/:mode;
// the legacy /p/:id engine-editor shell it replaced was removed in SH2.2.
import { useEffect, useRef, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { ErrorBoundary } from './design/ErrorBoundary';
import { ToastProvider } from './design/toast';

import { AuthProvider, useAuth } from './auth/AuthProvider';
import { NotFoundScreen } from './components/ui-shared';
import { NavigateBridge, navigate, useRoute } from './design/router';
import { setAuthExpiredHandler } from './services/api';

import { ScreenAccount, ScreenResetPassword, ScreenVerifyEmail, ScreenWelcome } from './account/screens-account';
import { ScreenAuth, ScreenComingSoon, ScreenDashboard } from './app/screens-app';
import { SIGNUP_OPEN } from './config/studioStatus';
import { consumeSignupIntent } from './services/auth';
import { ScreenDocsHome, ScreenDocsPage } from './docs/screens-docs';
import { DOC_PAGES, DOC_REDIRECTS } from './docs/docsContent';
import { ScreenBlog, ScreenBlogPost, ScreenChangelog } from './marketing/screens-blog';
import { ScreenAbout, ScreenAttributions, ScreenContact, ScreenLegal } from './marketing/screens-company';
import { ScreenFeaturePage } from './marketing/screens-feature-pages';
import { ScreenFeatures } from './marketing/screens-features';
import { ScreenHome } from './marketing/screens-home';
import { ScreenPricing } from './marketing/screens-pricing';
import { ScreenSunder } from './marketing/screens-sunder';
import { ScreenNinelives } from './marketing/screens-ninelives';
import { ScreenFurrow } from './marketing/screens-furrow';
import { ScreenDwindling } from './marketing/screens-dwindling';
import { ScreenMewsli } from './marketing/screens-mewsli';
import { ScreenGameLanding, ScreenPlayRuntime } from './play/screens-play';
import { ScreenDevProfile, ScreenShowcase } from './showcase/screens-showcase';
import { ScreenGameRoute } from './showcase/screens-game-route';

import {
  ScreenProjectAssets,
  ScreenProjectOverview,
  WorkspaceProvider,
} from './project/screens-project';
import {
  ScreenProjectPlayers,
  ScreenProjectPublish,
  ScreenProjectSettings,
  ScreenProjectVersions,
} from './project/screens-project-ops';
import { ScreenProjectWiki } from './wiki/screens-project-wiki';
import { ScreenWikiHome, ScreenWikiPage, ScreenWikisDirectory } from './wiki/screens-wiki-public';

import { BakeThumbnails } from './dev/BakeThumbnails';
import { EditorFixtureRoute } from './dev/EditorFixtureRoute';
import { EditorRoute } from './editor/EditorHost';

function ScreenNotFound() {
  return (
    <NotFoundScreen
      className="ss-screen"
      style={{ height: '100vh', gap: 24, padding: undefined }}
      glyphSize={48}
      title="404"
      titleStyle={{ fontSize: 52 }}
      body="This world isn't published. The link may be old, or the game was unlisted by its creator."
      bodyStyle={{ color: 'var(--ss-text-2)', maxWidth: 380, lineHeight: undefined }}
      ctas={[{ label: 'Back to Soulstack', to: '/' }]}
      ctaMarginTop={0}
    />
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="studio-loading">Loading…</div>;
  if (!user) return <Navigate to="/signin" replace />;
  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  // A Google sign-up leaves the SPA for the OAuth round trip and lands back
  // here on a fresh page load once Cognito resolves it - consumeSignupIntent
  // is how that's told apart from an ordinary Google sign-in, which lands on
  // this same guard. See markSignupIntent's call site (ScreenAuth) for why.
  if (!loading && user) return <Navigate to={consumeSignupIntent() ? '/app/welcome' : '/app'} replace />;
  return <>{children}</>;
}

/* Route param adapters for screens that take plain props. */
function FeaturePillarRoute() {
  const { pillar = 'editor' } = useParams();
  return <ScreenFeaturePage pillar={pillar} />;
}
function DevProfileRoute() {
  const { slug = '' } = useParams();
  return <ScreenDevProfile slug={slug} />;
}
function LegalRoute() {
  const { doc } = useParams();
  return <ScreenLegal doc={doc} />;
}
function GameLandingRoute() {
  const { slug = '' } = useParams();
  return <ScreenGameLanding slug={slug} />;
}
function PlayRuntimeRoute() {
  const { slug = '' } = useParams();
  return <ScreenPlayRuntime key={slug} slug={slug} />;
}
function WikiHomeRoute() {
  const { slug = '' } = useParams();
  return <ScreenWikiHome key={slug} slug={slug} />;
}
function WikiPageRoute() {
  const { slug = '', pageSlug = '' } = useParams();
  return <ScreenWikiPage key={`${slug}/${pageSlug}`} slug={slug} pageSlug={pageSlug} />;
}


/** Scroll container matching the design template's site-scroll behaviour. */
function SiteScroll({ children }: { children: ReactNode }) {
  const route = useRoute();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = 0;
  }, [route]);
  return (
    <div className="site-scroll" ref={ref} data-screen-label={route}>
      {children}
    </div>
  );
}

function SiteRoutes() {
  return (
    <SiteScroll>
      <Routes>
        {/* Marketing */}
        <Route path="/" element={<ScreenHome />} />
        <Route path="/features" element={<ScreenFeatures />} />
        <Route path="/features/:pillar" element={<FeaturePillarRoute />} />
        <Route path="/pricing" element={<ScreenPricing />} />
        <Route path="/showcase" element={<ScreenShowcase />} />
        <Route path="/showcase/:slug" element={<ScreenGameRoute />} />
        <Route path="/sunder" element={<ScreenSunder />} />
        <Route path="/ninelives" element={<ScreenNinelives />} />
        <Route path="/furrow" element={<ScreenFurrow />} />
        <Route path="/dwindling" element={<ScreenDwindling />} />
        <Route path="/mewsli" element={<ScreenMewsli />} />
        {import.meta.env.DEV && <Route path="/dev/bake-thumbs" element={<BakeThumbnails />} />}
        {import.meta.env.DEV && <Route path="/dev/editor-fixture/:mode?" element={<EditorFixtureRoute />} />}
        <Route path="/dev/:slug" element={<DevProfileRoute />} />
        <Route path="/blog" element={<ScreenBlog />} />
        <Route path="/blog/post/:id" element={<ScreenBlogPost />} />
        <Route path="/changelog" element={<ScreenChangelog />} />
        <Route path="/about" element={<ScreenAbout />} />
        <Route path="/contact" element={<ScreenContact />} />
        <Route path="/legal" element={<LegalRoute />} />
        <Route path="/legal/:doc" element={<LegalRoute />} />
        <Route path="/attributions" element={<ScreenAttributions />} />

        {/* Docs: one route per content page, plus the old addresses */}
        <Route path="/docs" element={<ScreenDocsHome />} />
        {DOC_PAGES.filter((p) => p.path !== '/docs').map((p) => (
          <Route key={p.path} path={p.path} element={<ScreenDocsPage page={p} />} />
        ))}
        {Object.entries(DOC_REDIRECTS).map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}

        {/* Play surface */}
        <Route path="/play/:slug" element={<GameLandingRoute />} />
        <Route path="/play/:slug/live" element={<PlayRuntimeRoute />} />

        {/* Wiki surface */}
        <Route path="/wikis" element={<ScreenWikisDirectory />} />
        <Route path="/wiki/:slug" element={<WikiHomeRoute />} />
        <Route path="/wiki/:slug/:pageSlug" element={<WikiPageRoute />} />

        {/* Auth */}
        <Route
          path="/signin"
          element={
            <RedirectIfAuthed>
              <ScreenAuth mode="signin" fill />
            </RedirectIfAuthed>
          }
        />
        <Route
          path="/signup"
          element={
            <RedirectIfAuthed>
              {SIGNUP_OPEN ? <ScreenAuth mode="signup" fill /> : <ScreenComingSoon fill />}
            </RedirectIfAuthed>
          }
        />
        <Route path="/verify" element={<ScreenVerifyEmail />} />
        <Route path="/reset" element={<ScreenResetPassword />} />

        {/* App (authenticated) */}
        <Route
          path="/app"
          element={
            <RequireAuth>
              <ScreenDashboard fill />
            </RequireAuth>
          }
        />
        <Route
          path="/app/welcome"
          element={
            <RequireAuth>
              <ScreenWelcome />
            </RequireAuth>
          }
        />
        <Route
          path="/app/account"
          element={
            <RequireAuth>
              <ScreenAccount />
            </RequireAuth>
          }
        />
        <Route
          path="/app/project/:id"
          element={
            <RequireAuth>
              <WorkspaceProvider />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<ScreenProjectOverview />} />
          <Route path="assets" element={<ScreenProjectAssets />} />
          <Route path="publish" element={<ScreenProjectPublish />} />
          <Route path="versions" element={<ScreenProjectVersions />} />
          <Route path="wiki" element={<ScreenProjectWiki />} />
          <Route path="players" element={<ScreenProjectPlayers />} />
          <Route path="settings" element={<ScreenProjectSettings />} />
        </Route>
        <Route
          path="/app/editor/:id/:mode?"
          element={
            <RequireAuth>
              <EditorRoute />
            </RequireAuth>
          }
        />

        <Route path="*" element={<ScreenNotFound />} />
      </Routes>
    </SiteScroll>
  );
}

/** Site root: auth context + history router + the design's route map. */
/**
 * Route to sign-in whenever the API reports the session has expired (401), so a
 * stale token surfaces as a redirect rather than a raw error deep in the UI.
 */
function AuthExpiryBridge() {
  useEffect(() => {
    setAuthExpiredHandler(() => navigate('/signin'));
    return () => setAuthExpiredHandler(null);
  }, []);
  return null;
}

export function SiteApp() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <NavigateBridge />
            <AuthExpiryBridge />
            <SiteRoutes />
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
